
    import router from '@/router'
    import { ref, watch, computed } from "vue"
    import { isAdmin, isBoss } from "@/utils/auth"
    import LogView from "@/components/LogView.vue"

    export default({
        name: "profileView",
        components: { LogView },
        props: {
            baseUrl: String,
            officerId: {
                type: String,
                default: null
            },
            isAddMode: {
                type: Boolean,
                default: false
            }
        },
        emits: ['saved'],
        setup(props: any, { emit }: any) {
            const isSaving = ref(false)
            const canEditCompany = computed(() => props.isAddMode || isAdmin() || isBoss())
            // Officer change history holds pay and bank details, so only admin/Boss can see it
            const canViewLog = computed(() => isAdmin() || isBoss())
            const officerFieldMappings = {
                PayRate: 'Pay Rate',
                SecurityCompanyId: 'Company',
                DesignationId: 'Designation',
                DeptId: 'Department',
                SiaExpiryDate: 'SIA Expiry',
                SiabadageNumber: 'SIA Badge Number',
                Ninumber: 'NI Number',
                ShareCode: 'Share Code',
                ShareCodeUpdatedAt: 'Share Code Updated',
                UpdatedOn: 'Updated On',
                PassportNo: 'Passport No',
                PassportExpiryDate: 'Passport Expiry',
                VisaExpiryDate: 'Visa Expiry',
                AccountNumber: 'Account Number',
                SortCode: 'Sort Code',
                AccountTitle: 'Account Title',
                IssueDate: 'Issue Date',
                ExpiryDate: 'Expiry Date',
                Number: 'Document Number',
                CityId: 'City',
                GenderId: 'Gender',
                CountryOfBirthId: 'Country of Birth',
                NationalityId: 'Nationality',
                AddressCountryId: 'Address Country',
                CountryOfIssueId: 'Country of Issue',
                ConveyanceId: 'Conveyance',
                TypeId: 'Document Type',
                OfficerGroupId: 'Officer Group',
            }
            // Turn ids in the log into names using the lists this form already loads
            const toLabels = (list: any[], idKey: string, nameKey: string) =>
                Object.fromEntries(list.map((item: any) => [item[idKey], item[nameKey]]))
            const officerValueLabels = computed(() => {
                const countries = toLabels(nationalityList.value, 'id', 'name')
                const lookupData = JSON.parse(localStorage.getItem("lookupData") || "{}")
                return {
                    DesignationId: toLabels(lookupData.designations || [], 'id', 'name'),
                    DeptId: toLabels(lookupData.departments || [], 'id', 'name'),
                    OfficerGroupId: toLabels(lookupData.officerGroups || [], 'id', 'name'),
                    CityId: toLabels(cityList.value, 'cityId', 'name'),
                    GenderId: toLabels(genderList.value, 'id', 'name'),
                    CountryOfBirthId: countries,
                    NationalityId: countries,
                    AddressCountryId: countries,
                    CountryOfIssueId: countries,
                    SecurityCompanyId: toLabels(companiesList.value, 'companyId', 'companyName'),
                    ConveyanceId: toLabels(conveyanceListArray.value, 'id', 'name'),
                    TypeId: toLabels(documentTypesList.value, 'id', 'name'),
                }
            })
            const isDataLoaded = ref(false)
            
            const companiesList = ref<any[]>([])
            const cityList = ref<any[]>([])
            const genderList = ref<any[]>([])
            const documentTypesList = ref<any[]>([])
            const nationalityList = ref<any[]>([])
            const conveyanceListArray = ref([
                { id: 1, name: "Car" }, 
                { id: 2, name: "Motor Bike" }, 
                { id: 3, name: "Public Tpt" }, 
                { id: 4, name: "Bike" }
            ])
            
            const selectedConveyanceName = ref("")
            const selectedCityName = ref("")
            const selectedGenderType = ref("")
            const selectedNationality = ref("")
            // British officers are exempt from the right-to-work check, so they don't need a share code.
            // Country Id 21 = United Kingdom.
            const isUkNational = computed(() =>
                nationalityList.value.find((n: any) => n.name === selectedNationality.value)?.id === 21)
            const selectedCountryOfBirth = ref("")
            const selectedAddressCountry = ref("")
            const selectedIdDocumentCountryOfIssue = ref("")
            const selectedIdDocumentName = ref("")
            
            const idDocumentNumber = ref("")
            const idDocumentIssueDate = ref("")
            const idDocumentExpiryDate = ref("")
            const idDocumentId = ref<number | null>(null)
            
            const formData = ref({
                SecurityOfficerId: 0,
                FirstName: '',
                LastName: '',
                PreviouslyKnownAs: '',
                MobileNumber: '',
                Email: '',
                Phone2: '',
                SiabadageNumber: '',
                SiaExpiryDate: '',
                Ninumber: '',
                ShareCode: '',
                ShareCodeUpdatedAt: '',
                HouseNameNumber: '',
                StreetAdress: '',
                County: '',
                PostCode: '',
                NextOfKin: '',
                DateOfBirth: '',
                PlaceOfBirth: '',
                PassportNo: '',
                PassportExpiryDate: '',
                VisaExpiryDate: '',
                SecurityCompanyId: '',
                Active: true,
                Town: '',
                PassportId: null,
                IdentityDocumentId: null,
                PayRate: '0',
                CityId: 0,
                ConveyanceId: 0
            })
            
            const isPrivacyPolicyAgreed = ref(false)

            const formatDateForInput = (apiDate: string | null): string => {
                if (!apiDate) return ""
                const match = apiDate.match(/^(\d{4}-\d{2}-\d{2})/)
                return match ? match[1] : ""
            }

            const formatDateForDisplay = (dateValue: string) => {
                if (!dateValue) return ""
                const parts = dateValue.split('-')
                if (parts.length === 3) {
                    return `${parts[2]}/${parts[1]}/${parts[0]}`
                }
                return dateValue
            }

            const loadData = async () => {
                //console.log("=== loadData called, isAddMode:", props.isAddMode, "officerId:", props.officerId)
                isDataLoaded.value = false

                const token = localStorage.token
                const headers = {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }

                try {
                    const [companiesRes, utilityRes, citiesRes] = await Promise.all([
                        fetch(props.baseUrl + "Companies/Companies", { headers }),
                        fetch(props.baseUrl + "SecurityOfficers/GenderAndDocumentTypesAndNationalityList", { headers }),
                        fetch(props.baseUrl + "Cities/Cities", { headers })
                    ])

                    const companiesData = await companiesRes.json()
                    const utilityData = await utilityRes.json()
                    const citiesData = await citiesRes.json()

                    companiesList.value = companiesData || []
                    genderList.value = utilityData?.utilityService?.gendersList || []

                    //ocumentTypesList.value = utilityData.utilityService.documentTypesList.filter((d: any) => d.id === 2) || [];
                    documentTypesList.value = utilityData.utilityService.documentTypesList || [];
                    
                    //console.log("utilityData?.utilityService?.documentTypesList= ", utilityData?.utilityService?.documentTypesList?.find((d: any) => d.id === 1));

                    nationalityList.value = utilityData?.utilityService?.nationalityList || []
                    cityList.value = citiesData?.cities || []

                    //console.log("Lists loaded - cities:", cityList.value.length, "nationalities:", nationalityList.value.length)

                    if (!props.isAddMode && props.officerId) {
                        //console.log("Edit mode - loading officer data for ID:", props.officerId)
                        
                        const profileRes = await fetch(props.baseUrl + "Securityofficers/OfficerFullDetailById", {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ "Id": parseInt(props.officerId) })
                        })
                        if (!profileRes.ok) {
                            throw new Error(`Failed to load officer data: HTTP ${profileRes.status}`)
                        }
                        const profileData = await profileRes.json()

                        const data = profileData.officerFullDetailViewModel
                        if (data) {
                            formData.value = {
                                SecurityOfficerId: data.securityOfficerId || 0,
                                FirstName: data.firstName || '',
                                LastName: data.lastName || '',
                                PreviouslyKnownAs: data.previouslyKnownAs || '',
                                MobileNumber: data.mobileNumber || '',
                                Email: data.email || '',
                                Phone2: data.phone2 || '',
                                SiabadageNumber: data.siabadageNumber || '',
                                SiaExpiryDate: formatDateForInput(data.siaExpiryDate),
                                Ninumber: data.ninumber || '',
                                ShareCode: data.shareCode || '',
                                ShareCodeUpdatedAt: formatDateForInput(data.shareCodeUpdatedAt),
                                HouseNameNumber: data.houseNameNumber || '',
                                StreetAdress: data.streetAdress || '',
                                County: data.county || '',
                                PostCode: data.postCode || '',
                                NextOfKin: data.nextOfKin || '',
                                DateOfBirth: formatDateForInput(data.dateOfBirth),
                                PlaceOfBirth: data.placeOfBirth || '',
                                PassportNo: data.passport?.passportNo || '',
                                PassportExpiryDate: formatDateForInput(data.passport?.passportExpiryDate),
                                VisaExpiryDate: formatDateForInput(data.passport?.visaExpiryDate),
                                SecurityCompanyId: data.securityCompanyId || '',
                                Active: data.active ?? true,
                                Town: data.town || '',
                                PassportId: data.passportId || null,
                                IdentityDocumentId: data.identityDocumentId || null,
                                PayRate: data.payRate || '0',
                                CityId: data.cityId || 0,
                                ConveyanceId: data.conveyanceId || 0
                            }

                            const cityObj = cityList.value.find((c: any) => c.cityId === data.cityId)
                            const genderObj = genderList.value.find((g: any) => g.id === data.genderId)
                            const nationalityObj = nationalityList.value.find((n: any) => n.id === data.nationalityId)
                            const birthCountryObj = nationalityList.value.find((n: any) => n.id === data.countryOfBirthId)
                            const addressCountryObj = nationalityList.value.find((n: any) => n.id === data.addressCountryId)
                            
                            selectedCityName.value = cityObj?.name || ''
                            selectedGenderType.value = genderObj?.name || ''
                            selectedNationality.value = nationalityObj?.name || ''
                            selectedCountryOfBirth.value = birthCountryObj?.name || ''
                            selectedAddressCountry.value = addressCountryObj?.name || ''
                            selectedConveyanceName.value = conveyanceListArray.value.find(c => c.id === data.conveyanceId)?.name || ''
                            
                            if (data.identityDocument) {
                                idDocumentNumber.value = data.identityDocument.number || ''
                                idDocumentIssueDate.value = formatDateForInput(data.identityDocument.issueDate)
                                idDocumentExpiryDate.value = formatDateForInput(data.identityDocument.expiryDate)
                                idDocumentId.value = data.identityDocument.id || null
                                
                                const docCountryObj = nationalityList.value.find((n: any) => n.id === data.identityDocument.countryOfIssueId)
                                selectedIdDocumentCountryOfIssue.value = docCountryObj?.name || ''
                                
                                const docTypeObj = documentTypesList.value.find((d: any) => d.id === data.identityDocument.typeId)
                                selectedIdDocumentName.value = docTypeObj?.name || ''
                            }
                        }
                    } else {
                        //console.log("Add mode - keeping empty form")
                        formData.value = {
                            SecurityOfficerId: 0,
                            FirstName: '',
                            LastName: '',
                            PreviouslyKnownAs: '',
                            MobileNumber: '',
                            Email: '',
                            Phone2: '',
                            SiabadageNumber: '',
                            SiaExpiryDate: '',
                            Ninumber: '',
                            ShareCode: '',
                            ShareCodeUpdatedAt: '',
                            HouseNameNumber: '',
                            StreetAdress: '',
                            County: '',
                            PostCode: '',
                            NextOfKin: '',
                            DateOfBirth: '',
                            PlaceOfBirth: '',
                            PassportNo: '',
                            PassportExpiryDate: '',
                            VisaExpiryDate: '',
                            SecurityCompanyId: '',
                            Active: true,
                            Town: '',
                            PassportId: null,
                            IdentityDocumentId: null,
                            PayRate: '0',
                            CityId: 0,
                            ConveyanceId: 0
                        }
                        selectedCityName.value = ''
                        selectedGenderType.value = ''
                        selectedNationality.value = ''
                        selectedCountryOfBirth.value = ''
                        selectedAddressCountry.value = ''
                        selectedConveyanceName.value = ''
                        selectedIdDocumentName.value = ''
                        selectedIdDocumentCountryOfIssue.value = ''
                        idDocumentNumber.value = ''
                        idDocumentIssueDate.value = ''
                        idDocumentExpiryDate.value = ''
                        idDocumentId.value = null
                    }
                    
                    isDataLoaded.value = true
                } catch (error) {
                    console.error("Load error:", error)
                    isDataLoaded.value = true
                    alert("Failed to load officer data. Please try again.")
                }
            }
                    
            const UpdateOfficer = async () => {
                

                if (!props.isAddMode && !formData.value.SecurityOfficerId) {
                    alert("Invalid officer data")
                    return
                }

                const cityObj = cityList.value.find((c: any) => c.name === selectedCityName.value)
                if (!cityObj) { 
                    alert(`Please select a valid city. "${selectedCityName.value}" not found.`)
                    return 
                }
                const cityId = cityObj.cityId

                const genderObj = genderList.value.find((g: any) => g.name === selectedGenderType.value)
                if (!genderObj) { 
                    alert(`Please select a valid gender. "${selectedGenderType.value}" not found.`)
                    return 
                }
                const genderId = genderObj.id

                const nationalityObj = nationalityList.value.find((n: any) => n.name === selectedNationality.value)
                if (!nationalityObj) { 
                    alert(`Please select a valid nationality. "${selectedNationality.value}" not found.`)
                    return 
                }
                const nationalityId = nationalityObj.id

                const countryOfBirthObj = nationalityList.value.find((n: any) => n.name === selectedCountryOfBirth.value)
                if (!countryOfBirthObj) { 
                    alert(`Please select a valid country of birth. "${selectedCountryOfBirth.value}" not found.`)
                    return 
                }
                const countryOfBirthId = countryOfBirthObj.id

                const addressCountryObj = nationalityList.value.find((n: any) => n.name === selectedAddressCountry.value)
                if (!addressCountryObj) { 
                    alert(`Please select a valid address country. "${selectedAddressCountry.value}" not found.`)
                    return 
                }
                const addressCountryId = addressCountryObj.id

                const conveyanceObj = conveyanceListArray.value.find(c => c.name === selectedConveyanceName.value)
                if (!conveyanceObj) { 
                    alert(`Please select a valid conveyance. "${selectedConveyanceName.value}" not found.`)
                    return 
                }
                const conveyanceId = conveyanceObj.id

                const documentTypeObj = documentTypesList.value.find((d: any) => d.name === selectedIdDocumentName.value)
                if (!documentTypeObj && selectedIdDocumentName.value) { 
                    alert(`Please select a valid document type. "${selectedIdDocumentName.value}" not found.`)
                    return 
                }
                const documentTypeId = documentTypeObj?.id || null

                const idDocumentCountryObj = nationalityList.value.find((n: any) => n.name === selectedIdDocumentCountryOfIssue.value)
                if (!idDocumentCountryObj && selectedIdDocumentCountryOfIssue.value) { 
                    alert(`Please select a valid document country of issue. "${selectedIdDocumentCountryOfIssue.value}" not found.`)
                    return 
                }
                const idDocumentCountryId = idDocumentCountryObj?.id || null

                isSaving.value = true

                let payload;
                
                if (props.isAddMode) {
                    payload = {
                        FirstName: formData.value.FirstName,
                        LastName: formData.value.LastName,
                        PostCode: formData.value.PostCode,
                        CityId: cityId,
                        County: formData.value.County,
                        StreetAdress: formData.value.StreetAdress,
                        Town: formData.value.Town || "",
                        Active: formData.value.Active,
                        SecurityCompanyId: parseInt(formData.value.SecurityCompanyId) || 0,
                        SiabadageNumber: formData.value.SiabadageNumber,
                        MobileNumber: formData.value.MobileNumber,
                        Ninumber: formData.value.Ninumber,
                        ShareCode: formData.value.ShareCode,
                        DesignationId: null,
                        DeptId: null,
                        ConveyanceId: conveyanceId,
                        Email: formData.value.Email,
                        SiaExpiryDate: formData.value.SiaExpiryDate,
                        Phone2: formData.value.Phone2 || "",
                        PayRate: formData.value.PayRate || "",
                        NextOfKin: formData.value.NextOfKin,
                        PreviouslyKnownAS: formData.value.PreviouslyKnownAs || "",
                        DateOfBirth: formData.value.DateOfBirth,
                        PlaceOfBirth: formData.value.PlaceOfBirth,
                        HouseNameNumber: formData.value.HouseNameNumber,
                        CountryOfBirthId: countryOfBirthId,
                        AddressCountryId: addressCountryId,
                        NationalityId: nationalityId,
                        GenderId: genderId,
                        IdentityDocument: {
                            Id: 0,
                            IssueDate: idDocumentIssueDate.value || null,
                            ExpiryDate: idDocumentExpiryDate.value || null,
                            CountryOfIssueId: idDocumentCountryId,
                            Number: idDocumentNumber.value || "",
                            TypeId: documentTypeId
                        },
                        Passport: {
                            PassportNo: formData.value.PassportNo || "",
                            PassportExpiryDate: formData.value.PassportExpiryDate || null,
                            VisaExpiryDate: formData.value.VisaExpiryDate || null,
                            Nationality: nationalityId || ""
                        }
                    }
                } else {
                    payload = {
                        SecurityOfficerId: formData.value.SecurityOfficerId,
                        FirstName: formData.value.FirstName,
                        LastName: formData.value.LastName,
                        PostCode: formData.value.PostCode,
                        CityId: cityId,
                        County: formData.value.County,
                        StreetAdress: formData.value.StreetAdress,
                        Town: formData.value.Town || "",
                        Active: formData.value.Active,
                        SecurityCompanyId: parseInt(formData.value.SecurityCompanyId) || 0,
                        SiabadageNumber: formData.value.SiabadageNumber,
                        MobileNumber: formData.value.MobileNumber,
                        Ninumber: formData.value.Ninumber,
                        ShareCode: formData.value.ShareCode,
                        DesignationId: null,
                        DeptId: null,
                        PassportId: formData.value.PassportId,
                        ConveyanceId: conveyanceId,
                        Email: formData.value.Email,
                        SiaExpiryDate: formData.value.SiaExpiryDate,
                        Phone2: formData.value.Phone2 || "",
                        PayRate: formData.value.PayRate || "",
                        NextOfKin: formData.value.NextOfKin,
                        PreviouslyKnownAS: formData.value.PreviouslyKnownAs || "",
                        DateOfBirth: formData.value.DateOfBirth,
                        PlaceOfBirth: formData.value.PlaceOfBirth,
                        HouseNameNumber: formData.value.HouseNameNumber,
                        IdentityDocumentId: idDocumentId.value,
                        CountryOfBirthId: countryOfBirthId,
                        AddressCountryId: addressCountryId,
                        NationalityId: nationalityId,
                        GenderId: genderId,
                        IdentityDocument: {
                            Id: idDocumentId.value || 0,
                            IssueDate: idDocumentIssueDate.value || null,
                            ExpiryDate: idDocumentExpiryDate.value || null,
                            CountryOfIssueId: idDocumentCountryId,
                            Number: idDocumentNumber.value || "",
                            TypeId: documentTypeId
                        },
                        Passport: {
                            PassportNo: formData.value.PassportNo || "",
                            PassportExpiryDate: formData.value.PassportExpiryDate || null,
                            VisaExpiryDate: formData.value.VisaExpiryDate || null,
                            Nationality: ""
                        }
                    }
                }

                console.log("Sending payload:", payload)

                const endpoint = props.isAddMode
                    ? "SecurityOfficers/AddSecurityOfficer"
                    : "SecurityOfficers/UpdateSecurityOfficer"

                try {
                    const response = await fetch(props.baseUrl + endpoint, {
                        method: "POST",
                        headers: {
                            "Authorization": `Bearer ${localStorage.token}`,
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify(payload)
                    })

                    const responseText = await response.text()
                    //console.log("Response:", response.status, responseText)

                    if (response.ok) {
                        alert(props.isAddMode ? "Officer added successfully!" : "Profile updated successfully!");
                        emit('saved');
                        
                        const closeButton = document.querySelector('#UpdateOfficerModal .btn-close') as HTMLElement;
                        if (closeButton) {
                            closeButton.click();
                        }
                        
                        setTimeout(() => {
                            const backdrops = document.querySelectorAll('.modal-backdrop');
                            backdrops.forEach(backdrop => backdrop.remove());
                            document.body.classList.remove('modal-open');
                        }, 150);
                    } else {
                        alert(`${props.isAddMode ? 'Add' : 'Update'} failed: ${response.status} ${responseText}`)
                    }
                } catch (error) {
                    console.error("Error:", error)
                    alert("An error occurred. Please try again.")
                } finally {
                    isSaving.value = false
                }
            }

            watch(() => props.officerId, (newId) => {
                //console.log("Watch triggered with ID:", newId);
                loadData();
            }, { immediate: true })

            return {
                isDataLoaded,
                canEditCompany,
                canViewLog,
                officerFieldMappings,
                officerValueLabels,
                formData,
                companiesList,
                cityList,
                genderList,
                documentTypesList,
                nationalityList,
                conveyanceListArray,
                selectedConveyanceName,
                selectedCityName,
                selectedGenderType,
                selectedNationality,
                isUkNational,
                selectedCountryOfBirth,
                selectedAddressCountry,
                selectedIdDocumentCountryOfIssue,
                selectedIdDocumentName,
                idDocumentNumber,
                idDocumentIssueDate,
                idDocumentExpiryDate,
                isPrivacyPolicyAgreed,
                isSaving,
                formatDateForDisplay,
                UpdateOfficer,
                //isAddMode: props.isAddMode
            }
        },
    })
